import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Layout identique à la vraie page candidature
import SuiviHeader from '../../../../composants/SuiviHeader/SuiviHeader';
import TabNav from '../../../../composants/TabNav/TabNav';
import BottomActionBar from '../DetailCandidature/BottomActionBar';
import { getStatusColor } from '../../../../utils/entityUtils';
import { useLotImage } from '../../../../utils/getLotImage';
import { getProfile } from '../../../../services/profileService';
import '../DetailCandidature/DetailCandidature.css';
import '../DetailCandidature/TabQuestionsReponses.css';
import './DemoDetailCandidature.css';

// Composants réutilisés SANS MODIFICATION depuis DetailCandidature/
import SectionCandidat from '../DetailCandidature/SectionCandidat';
import SectionChoixLots from '../DetailCandidature/SectionChoixLots';
import SectionOffreTechnique from '../DetailCandidature/SectionOffreTechnique';
import SectionOffreFinanciere, {
  type Article,
  type PrixLigne,
  type RegionGroup,
} from '../DetailCandidature/SectionOffreFinanciere';
import SectionSoumission from '../DetailCandidature/SectionSoumission';
import SectionPliable from '../../../../composants/SectionPliable/SectionPliable';
import Button from '../../../../composants/Button/Button';
import Banner from '../../../../composants/Banner/Banner';
import DetailCandidatureSkeleton from '../DetailCandidature/DetailCandidatureSkeleton';

// Types depuis DemoInitModal (partagés pour éviter la duplication)
import type { DemoZone, DemoArticle } from '../DemoInitModal';

// ── Constantes ────────────────────────────────────────────────────────────────

const DEFAULT_PRIX_LIGNE: PrixLigne = { prix_a: '', prix_b: '', prix_c: '' };

const TABS_DEMO = ['Avis et documents', 'Candidature', 'Questions / Réponses'];

// Données fictives de l'accord-cadre affiché dans le header
const MOCK_ACCORD_CADRE = {
  nom: 'Accord-Cadre Fictif',
  acheteur: 'Direction des Achats Publics',
  reference_avis: 'AC-2027-DÉMO',
};

// Entité par défaut (fallback si l'API échoue)
const ENTITE_FALLBACK = {
  id: 0,
  nom: '',
  siret: '',
  representant: undefined as string | undefined,
  email: undefined as string | undefined,
  telephone: undefined as string | undefined,
  progression: 100,
};

// ── Helper ────────────────────────────────────────────────────────────────────

function buildRegionGroups(lotsDisponibles: DemoZone[], selectedIds: number[]): RegionGroup[] {
  const map = new Map<number, RegionGroup>();

  lotsDisponibles
    .filter((l) => selectedIds.includes(l.id))
    .forEach((l) => {
      const regionId = l.departement?.region?.id ?? l.departement?.id ?? 0;
      const regionNom = l.departement?.region?.nom ?? l.departement?.nom ?? 'Région inconnue';
      if (!map.has(regionId)) map.set(regionId, { regionId, regionNom, lots: [] });
      map.get(regionId)!.lots.push({
        id: l.id,
        departement: l.departement as { id: number; nom: string } | undefined,
      });
    });

  return Array.from(map.values()).sort((a, b) => a.regionNom.localeCompare(b.regionNom));
}

// ── Type du route state (injecté par DemoInitModal) ──────────────────────────

interface DemoRouteState {
  accordCadreId: number;
  lotTechnique: { id: number; code: string; nom: string; cpv?: string };
  lotsDisponibles: DemoZone[];
  articles: DemoArticle[];
  initialSelectedZoneIds?: number[];
}

// ── Composant principal ──────────────────────────────────────────────────────

const DemoDetailCandidature: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const demoState = location.state as DemoRouteState | null;

  // ── Guard : accès direct sans données → message + retour ────────────────────
  if (!demoState?.lotTechnique) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Aucune simulation en cours.</p>
          <button
            className="px-4 py-2 bg-[#f97316] text-white rounded-lg hover:bg-orange-600"
            onClick={() => navigate('/publications')}
          >
            Retour aux publications
          </button>
        </div>
      </div>
    );
  }

  const { lotTechnique, lotsDisponibles, articles, initialSelectedZoneIds } = demoState;

  // Détermine si le lot est régional (codes 1xx ou 3xx) ou départemental (2xx)
  // Cohérent avec SectionChoixLots.tsx et le modèle LotTechnique::isRegional()
  const lotCode = Number(lotTechnique.code);
  const isRegional = (lotCode >= 100 && lotCode < 200) || (lotCode >= 300 && lotCode < 400);

  // ── État local de la simulation ─────────────────────────────────────────────
  // Skeleton bref au montage : on calque l'experience de la vraie page (qui l'affiche pendant
  // le fetch). Demo statique -> court delai pour montrer la meme silhouette de chargement.
  const [bootSkeleton, setBootSkeleton] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setBootSkeleton(false), 600);
    return () => clearTimeout(t);
  }, []);
  const [activeTab, setActiveTab] = useState(1); // index 1 = "Candidature"
  const [entite, setEntite] = useState(ENTITE_FALLBACK);
  const [selectedLotIds, setSelectedLotIds] = useState<number[]>(
    initialSelectedZoneIds ?? []
  );
  const [offreTechnique, setOffreTechnique] = useState<Record<string, string>>({});
  const [prixIdentiques, setPrixIdentiques] = useState(true);
  const [prixLignes, setPrixLignes] = useState<Record<number, PrixLigne>>({});
  const [prixParLot, setPrixParLot] = useState<Record<number, Record<number, PrixLigne>>>({});
  const [prixParRegion, setPrixParRegion] = useState<Record<number, Record<number, PrixLigne>>>({});
  const [nomSignataire, setNomSignataire] = useState('');
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [isDepose, setIsDepose] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Auto-affiche le PDF démo quand la case CG est acceptée
  useEffect(() => {
    if (acceptConditions) {
      setPdfUrl('/demo/recap-candidature-demo.pdf');
      setTimeout(() => {
        document.getElementById('pdf-preview-zone')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else {
      setPdfUrl(null);
    }
  }, [acceptConditions]);

  // Charge le vrai profil de l'utilisateur connecté
  useEffect(() => {
    getProfile().then((profile) => {
      const e = profile.entite;
      const c = profile.coordonnees;
      const representant = c
        ? [c.legal_prenom, c.legal_nom].filter(Boolean).join(' ') || undefined
        : undefined;
      setEntite({
        id: e.id,
        nom: e.nom,
        siret: e.siret,
        representant: representant || undefined,
        email: c?.legal_email ?? undefined,
        telephone: c?.legal_tel ?? undefined,
        progression: 100, // progression forcée à 100 en démo
      });
    }).catch(() => { /* garde le fallback silencieusement */ });
  }, []);

  // ── Valeurs dérivées ────────────────────────────────────────────────────────

  const regionGroups: RegionGroup[] = useMemo(
    () => (isRegional ? buildRegionGroups(lotsDisponibles, selectedLotIds) : []),
    [isRegional, lotsDisponibles, selectedLotIds],
  );

  // selectedLots au format attendu par SectionOffreFinanciere
  const selectedLotsForFinanciere = useMemo(
    () =>
      lotsDisponibles
        .filter((l) => selectedLotIds.includes(l.id))
        .map((l) => ({
          id: l.id,
          departement: l.departement as { id: number; nom: string } | undefined,
        })),
    [lotsDisponibles, selectedLotIds],
  );

  // Validation en temps réel pour SectionSoumission
  const validationItems = useMemo(() => {
    const hasLots = selectedLotIds.length > 0;
    const hasPrix =
      articles.length === 0 ||
      articles.every((a) => {
        const p = prixLignes[a.id];
        return p && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';
      });

    // Vérifie que tous les prix sont dégressifs (A >= B >= C) — s'applique à tous les articles en mode démo
    const prixOk = articles.every((a) => {
      const checkLine = (l: PrixLigne | undefined) => {
        if (!l) return true;
        const pA = parseFloat(l.prix_a ?? '');
        const pB = parseFloat(l.prix_b ?? '');
        const pC = parseFloat(l.prix_c ?? '');
        if (!isNaN(pA) && !isNaN(pB) && !isNaN(pC) && (pA > 0 || pB > 0 || pC > 0)) {
          return pA >= pB && pB >= pC;
        }
        return true;
      };
      if (!checkLine(prixLignes[a.id])) return false;
      for (const lotId of Object.keys(prixParLot)) {
        if (!checkLine(prixParLot[Number(lotId)]?.[a.id])) return false;
      }
      for (const regionId of Object.keys(prixParRegion)) {
        if (!checkLine(prixParRegion[Number(regionId)]?.[a.id])) return false;
      }
      return true;
    });

    // Vérifie que les 3 critères techniques obligatoires sont remplis
    const CRITERES_OBLIGATOIRES = ['description_humain', 'description_operationnel', 'description_environnement'];
    const hasTechnique = CRITERES_OBLIGATOIRES.every(
      (slug) => (offreTechnique[slug] ?? '').trim().length > 0
    );

    return [
      { label: 'Lots géographiques sélectionnés', ok: hasLots },
      { label: 'Offre technique complétée', ok: hasTechnique },
      { label: 'Prix renseignés pour tous les articles', ok: hasPrix },
      { label: 'Prix dégressifs ou égaux (Prix A ≥ Prix B ≥ Prix C)', ok: prixOk },
      { label: 'Nom du signataire renseigné', ok: nomSignataire.trim().length > 0 },
    ];
  }, [selectedLotIds, articles, prixLignes, prixParLot, prixParRegion, nomSignataire, offreTechnique]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleChangeCritere = useCallback((slug: string, value: string) => {
    setOffreTechnique((prev) => ({ ...prev, [slug]: value }));
  }, []);

  const handleChangePrix = useCallback(
    (articleId: number, champ: keyof PrixLigne, val: string) => {
      setPrixLignes((prev) => ({
        ...prev,
        [articleId]: {
          ...DEFAULT_PRIX_LIGNE,
          ...prev[articleId],
          [champ]: val,
        },
      }));
    },
    [],
  );

  const handleChangePrixLot = useCallback(
    (lotId: number, articleId: number, champ: keyof PrixLigne, val: string) => {
      setPrixParLot((prev) => ({
        ...prev,
        [lotId]: {
          ...prev[lotId],
          [articleId]: {
            ...DEFAULT_PRIX_LIGNE,
            ...prev[lotId]?.[articleId],
            [champ]: val,
          },
        },
      }));
    },
    [],
  );

  const handleChangePrixRegion = useCallback(
    (regionId: number, articleId: number, champ: keyof PrixLigne, val: string) => {
      setPrixParRegion((prev) => ({
        ...prev,
        [regionId]: {
          ...prev[regionId],
          [articleId]: {
            ...DEFAULT_PRIX_LIGNE,
            ...prev[regionId]?.[articleId],
            [champ]: val,
          },
        },
      }));
    },
    [],
  );

  const handleDeposer = () => {
    navigate('/publications', { state: { demoComplete: true } });
  };

  const lotImage = useLotImage(lotTechnique.nom);
  const status = isDepose ? 'soumise' : 'brouillon';

  const allValid = validationItems.every((item) => item.ok === true);

  // ── Rendu ────────────────────────────────────────────────────────────────────

  const renderTabContent = () => {
    if (activeTab === 0) {
      // ── Tab Avis et documents (version démo statique) ──────────────────────
      return (
        <div className="flex flex-col gap-8">
          <SectionPliable title="Avis de marché">
            <div className="section-content">
              <div className="avis-grid">
                <div className="avis-field"><label>Référence de l'avis</label><span>{MOCK_ACCORD_CADRE.reference_avis}</span></div>
                <div className="avis-field"><label>Acheteur</label><span>{MOCK_ACCORD_CADRE.acheteur}</span></div>
                <div className="avis-field"><label>Type de marché / CCAG</label><span>Travaux — CCAG Travaux</span></div>
                <div className="avis-field"><label>Catégorie CPV</label><span>54000000-7</span></div>
                <div className="avis-field"><label>Date limite de candidature</label><span>31/12/2050 à 12:00 (heure de Paris)</span></div>
                <div className="avis-field"><label>Délai de validité de l'offre</label><span>180 jours</span></div>
                <div className="avis-field"><label>Langue</label><span>Français</span></div>
                <div className="avis-field">
                  <label>Présentation de la consultation</label>
                  <span>Ceci est une simulation de candidature à un accord-cadre de travaux BTP. Aucune donnée n'est transmise ni enregistrée.</span>
                </div>
              </div>
            </div>
          </SectionPliable>

          <SectionPliable title="Documents de la consultation">
            <div className="section-content">
              <p style={{ fontSize: '0.95rem', color: '#9ca3af', fontStyle: 'italic' }}>
                Mode démo — Les documents officiels sont disponibles dans votre espace candidature réel.
              </p>
            </div>
          </SectionPliable>
        </div>
      );
    }

    if (activeTab === 2) {
      // ── Tab Questions / Réponses (version démo — structure identique au vrai) ──
      const DEMO_MESSAGES: Array<{
        id: number;
        contenu: string;
        auteur_type: 'admin' | 'prestataire';
        is_mine: boolean;
        created_at: string;
        numero?: number;
        identifiant_candidature?: string;
      }> = [
        {
          id: 1,
          contenu: `A t-on le droit de sous-traiter une partie des missions ?`,
          auteur_type: 'prestataire',
          is_mine: false,
          created_at: '2026-03-10T09:15:00.000Z',
          numero: 1,
          identifiant_candidature: 'DEMO-001',
        },
        {
          id: 2,
          contenu: `Oui, mais le DC4 est à envoyer à l'acheteur commanditaire concerné en phase d'exécution de l'accord-cadre.`,
          auteur_type: 'admin',
          is_mine: false,
          created_at: '2026-03-11T14:30:00.000Z',
        },
        {
          id: 3,
          contenu: `Comment faire, si je ne souhaite pas réaliser un bon de commande ou un marché subséquent dans quelques mois ?`,
          auteur_type: 'prestataire',
          is_mine: false,
          created_at: '2026-03-12T10:05:00.000Z',
          numero: 2,
          identifiant_candidature: 'DEMO-002',
        },
        {
          id: 4,
          contenu: `Aucun problème, il vous appartient de cliquer sur « accepter » ou « refuser » à chaque commande qui vous est envoyée.`,
          auteur_type: 'admin',
          is_mine: false,
          created_at: '2026-03-13T11:20:00.000Z',
        },
        {
          id: 5,
          contenu: `Nous n'avons pas assez de détail sur les opérations et les quantités, pouvez vous être plus précis ?`,
          auteur_type: 'prestataire',
          is_mine: false,
          created_at: '2026-03-14T08:45:00.000Z',
          numero: 3,
          identifiant_candidature: 'DEMO-003',
        },
        {
          id: 6,
          contenu: `A ce stade de l'accord-cadre, nous ne disposons pas de tous les éléments des futures opérations de nos acheteurs. C'est pourquoi une remise en concurrence à chaque marché subséquent sera réalisée sur la base des nouveaux éléments.`,
          auteur_type: 'admin',
          is_mine: false,
          created_at: '2026-03-15T16:00:00.000Z',
        },
      ];

      const formatDate = (iso: string) => {
        const d = new Date(iso);
        return (
          d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
          ' à ' +
          d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        );
      };

      const getAuthorLabel = (msg: typeof DEMO_MESSAGES[0]) => {
        if (msg.auteur_type === 'admin') return 'Centralis';
        if (msg.numero != null) return `Question ${msg.numero} - Candidature ${msg.identifiant_candidature}`;
        return msg.identifiant_candidature ? `Candidature ${msg.identifiant_candidature}` : 'Candidat';
      };

      return (
        <SectionPliable title="Questions / Réponses">
        <div className="qr-thread-container">
          <div className="qr-thread">
            {DEMO_MESSAGES.map((msg) => (
              <div
                key={msg.id}
                className={`qr-bubble ${msg.is_mine ? 'qr-bubble--mine' : ''} ${msg.auteur_type === 'admin' ? 'qr-bubble--admin' : 'qr-bubble--prestataire'}`}
              >
                <div className="qr-bubble-header">
                  <span className="qr-bubble-author">{getAuthorLabel(msg)}</span>
                  <span className="qr-bubble-date">{formatDate(msg.created_at)}</span>
                </div>
                <div className="qr-bubble-body">{msg.contenu}</div>
              </div>
            ))}
          </div>

          <div className="qr-input-bar">
            <span title="Indisponible en mode démo" style={{ display: 'block' }}>
              <textarea
                className="qr-input-textarea"
                disabled
                placeholder="Poser une question… (indisponible en mode démo)"
                rows={2}
                style={{ cursor: 'not-allowed', opacity: 0.6 }}
              />
            </span>
            <div className="qr-input-actions">
              <span className="qr-char-count">0 / 2000</span>
              <span title="Indisponible en mode démo" style={{ display: 'inline-block' }}>
                <button
                  disabled
                  style={{ padding: '0.4rem 1rem', background: '#f97316', color: '#fff', border: 'none', borderRadius: '6px', opacity: 0.4, cursor: 'not-allowed', fontSize: '0.875rem', fontWeight: 600 }}
                >
                  Envoyer
                </button>
              </span>
            </div>
          </div>
        </div>
        </SectionPliable>
      );
    }

    // activeTab === 1 — Candidature
    return (
      <div className="flex flex-col gap-8">

        {/* 1. Section Candidat — profil fictif, solo uniquement, invitation désactivée */}
        <SectionPliable title="1- Candidature">
        <SectionCandidat
          entite={entite}
          groupementMembers={[]}
          estMandataire={true}
          isSoumise={isDepose}
          canInvite={false}
          inviteTooltip="Indisponible en mode démo"
          onRetirer={() => {}}
          onInviter={() => {}}
        />
        {/* Invitation au groupement : bouton visible mais désactivé en mode démo (même taille que les autres). */}
        {!isDepose && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <span title="Indisponible en mode démo" style={{ display: 'inline-block' }}>
              <Button
                type="button"
                variant="primary"
                value="Ajouter un membre au groupement"
                size="small"
                disabled
              />
            </span>
          </div>
        )}
        </SectionPliable>

        {/* 2. Section Choix des lots géographiques */}
        <SectionPliable title="2- Choix des lots">
        <SectionChoixLots
          lotTechnique={lotTechnique}
          lotsDisponibles={lotsDisponibles}
          selectedIds={selectedLotIds}
          canEdit={!isDepose}
          onLotsGeoChange={setSelectedLotIds}
        />
        </SectionPliable>

        {/* 3. Section Offre technique (démo : pondération + barèmes figés sur les valeurs métier 55% et 4/5/1) */}
        <SectionPliable title="3- Offre technique (pondérée à 55%)">
        <Banner
          variant="info"
          message="Par défaut les réponses sont remplies avec les éléments de votre profil, mais il vous appartient de les mettre à jour concernant cette consultation."
        />
        <SectionOffreTechnique
          poidsOffreTechnique={55}
          baremes={{ humain: 4, operationnel: 5, environnement: 1 }}
          canEdit={!isDepose}
          offreTechnique={offreTechnique}
          onChangeCritere={handleChangeCritere}
        />
        </SectionPliable>

        {/* 4. Section Offre financière */}
        <SectionPliable title="4- Offre financière (pondérée à 45%)">
        <SectionOffreFinanciere
          poidsOffreFinanciere={45}
          articles={articles as Article[]}
          canEdit={!isDepose}
          isRegional={isRegional}
          regionGroups={regionGroups}
          prixIdentiques={prixIdentiques}
          prixLignes={prixLignes}
          prixParLot={prixParLot}
          prixParRegion={prixParRegion}
          selectedLots={selectedLotsForFinanciere}
          degressifForAll={true}
          onTogglePrixIdentiques={() => setPrixIdentiques((v) => !v)}
          onChangePrix={handleChangePrix}
          onChangePrixLot={handleChangePrixLot}
          onChangePrixRegion={handleChangePrixRegion}
        />
        </SectionPliable>

        {/* 5. Section Dépôt de la candidature */}
        <SectionPliable title="5- Dépôt de la candidature et de l'offre">
        <SectionSoumission
          isSoumise={isDepose}
          deposee_at={isDepose ? new Date().toISOString() : null}
          estMandataire={true}
          validationItems={validationItems}
          isDeposing={false}
          acceptConditions={acceptConditions}
          onToggleAccept={() => setAcceptConditions((v) => !v)}
          onDeposer={handleDeposer}
          cguUrl="/cgv"
          cguCheckboxPrefix="Je certifie que les informations fournies sont exactes et complètes, et j'accepte les"
          cguLinkText="conditions générales"
          pdfUrl={pdfUrl}
          nomSignataire={nomSignataire}
          onNomSignataireChange={setNomSignataire}
        />
        </SectionPliable>

      </div>
    );
  };

  if (bootSkeleton) {
    return <DetailCandidatureSkeleton />;
  }

  return (
    <div className="candidature-detail-container">

      {/* Header identique à la vraie page candidature — margin-bottom annulé pour coller au bandeau */}
      <div className="no-print" style={{ marginBottom: 0 }}>
        <div style={{ marginBottom: 0 }} className="demo-header-no-gap">
          <SuiviHeader
            titre="Simulation de candidature et d'offre"
            sousTitre={MOCK_ACCORD_CADRE.nom}
            subtitle={`Candidature DÉMO · Réf. ${MOCK_ACCORD_CADRE.reference_avis}`}
            status={status}
            statusColor={getStatusColor(status)}
            delais="2050-12-31T11:00:00.000Z"
            depotLabel="Fin de dépôt : 31/12/2050 à 12h00 heure de Paris — Temps restant :"
            backgroundImage={lotImage}
            source="MS"
            retourLabel="Retour vers les candidatures"
            onRetour={() => navigate('/publications')}
            hideStatusBadge={true}
          />
        </div>
      </div>

      {/* Bandeau simulation */}
      <div className="no-print" style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '1.2rem 6rem', textAlign: 'center', fontSize: '1.15rem', color: '#92400e', fontWeight: 600 }}>
        Mode simulation — Aucune donnée n'est enregistrée
      </div>

      <div className="suivi-de-commande-content">
        <TabNav
          tabs={TABS_DEMO}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          disabledTabs={[]}
        />

        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      {!isDepose && (
        <BottomActionBar
          onQuitter={() => navigate('/publications')}
          onDeposer={handleDeposer}
          canDeposer={allValid && acceptConditions && pdfUrl !== null}
          estMandataire={true}
          saveStatus="idle"
        />
      )}

    </div>
  );
};

export default DemoDetailCandidature;
